import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, ExternalLink, Gift, Copy } from 'lucide-react';
import { publicApi } from '../lib/api';
import { BrandMark } from '../components/BrandMark';

type GiftMeta = {
  event_name: string;
  event_date: string | null;
  gift_bit_phone: string | null;
  gift_bit_link: string | null;
  bit_ready: boolean;
};

const QUICK = [100, 200, 300, 500, 1000];

function formatDate(date: string | null): string {
  if (!date) return '';
  try {
    return new Intl.DateTimeFormat('he-IL', {
      day: 'numeric', month: 'long', year: 'numeric',
    }).format(new Date(date));
  } catch {
    return date;
  }
}

function formatPhone(phone: string): string {
  const d = phone.replace(/\D/g, '');
  const local = d.startsWith('972') && d.length === 12 ? `0${d.slice(3)}` : d;
  if (/^05\d{8}$/.test(local)) return `${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6)}`;
  return phone;
}

export default function GiftPublicPage() {
  const { token = '' } = useParams();
  const [meta, setMeta] = useState<GiftMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [amount, setAmount] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [blessing, setBlessing] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [doneAmount, setDoneAmount] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    publicApi
      .get(`/public/gift/${token}`)
      .then((r) => alive && setMeta(r.data))
      .catch(() => alive && setNotFound(true))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [token]);

  const amountNum = useMemo(() => {
    const n = parseFloat(amount.replace(/,/g, ''));
    return Number.isFinite(n) ? n : 0;
  }, [amount]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setError('נא להזין שם');
      return;
    }
    if (amountNum <= 0) {
      setError('נא להזין סכום מתנה');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const r = await publicApi.post(`/public/gift/${token}`, {
        first_name: name.trim(),
        phone: phone.trim() || null,
        amount: amountNum,
        blessing: blessing.trim() || null,
      });
      const link = (r.data.gift_bit_link as string | null) || null;
      const bitPhone = (r.data.gift_bit_phone as string | null) || null;
      setDoneAmount(amountNum);

      if (link) {
        window.location.href = link;
        return;
      }
      if (bitPhone) {
        // אין deep-link ציבורי לסכום — מציגים מסך הצלחה עם המספר
        return;
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || 'שגיאה בשמירה');
    } finally {
      setSubmitting(false);
    }
  }

  async function copyPhone() {
    if (!meta?.gift_bit_phone) return;
    try {
      await navigator.clipboard.writeText(meta.gift_bit_phone.replace(/\D/g, '').replace(/^972/, '0') || meta.gift_bit_phone);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  }

  if (loading) {
    return (
      <div dir="rtl" className="min-h-full grid place-items-center p-6" role="status">
        <div className="text-center space-y-3">
          <div className="mx-auto h-10 w-10 rounded-full border-2 border-sage-200 border-t-sage-600 animate-spin" />
          <p className="text-ink-mute text-sm">טוען…</p>
        </div>
      </div>
    );
  }

  if (notFound || !meta) {
    return (
      <div dir="rtl" className="min-h-full grid place-items-center p-6">
        <div className="text-center max-w-sm surface p-8">
          <h1 className="font-display text-2xl font-bold text-ink">הקישור לא נמצא</h1>
          <p className="text-ink-mute mt-2 text-sm">בדקו עם הזוג שהקישור פעיל.</p>
        </div>
      </div>
    );
  }

  if (doneAmount != null) {
    return (
      <div dir="rtl" className="min-h-full px-4 py-10">
        <div className="mx-auto w-full max-w-md space-y-5 page-enter text-center">
          <BrandMark className="mx-auto" />
          <div className="surface p-8 space-y-4">
            <div className="mx-auto h-14 w-14 rounded-full bg-sage-100 grid place-items-center">
              <CheckCircle2 className="text-sage-700" size={28} aria-hidden />
            </div>
            <h1 className="font-display text-2xl font-bold text-ink">תודה על המתנה!</h1>
            <p className="text-ink-mute text-sm leading-relaxed">
              נרשם סכום של <strong className="text-ink">₪{doneAmount.toLocaleString('he-IL')}</strong> עבור {meta.event_name}.
            </p>
            {meta.gift_bit_link ? (
              <a
                href={meta.gift_bit_link}
                className="btn-primary w-full inline-flex items-center justify-center gap-2"
              >
                פתיחת ביט לתשלום
                <ExternalLink size={16} aria-hidden />
              </a>
            ) : null}
            {meta.gift_bit_phone ? (
              <div className="rounded-xl bg-ink/[0.03] border border-line p-4 space-y-2">
                <p className="text-sm text-ink-mute">העבירו בביט למספר:</p>
                <p className="text-2xl font-bold tracking-wide text-ink" dir="ltr">
                  {formatPhone(meta.gift_bit_phone)}
                </p>
                <button type="button" onClick={() => void copyPhone()} className="btn-secondary w-full inline-flex items-center justify-center gap-2">
                  <Copy size={15} aria-hidden />
                  {copied ? 'הועתק!' : 'העתקת מספר'}
                </button>
              </div>
            ) : null}
            {!meta.bit_ready ? (
              <p className="text-sm text-ink-mute">הזוג יקבל את הרישום באפליקציה — תודה!</p>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div dir="rtl" className="min-h-full px-4 py-8">
      <div className="mx-auto w-full max-w-md space-y-5 page-enter">
        <header className="text-center space-y-2">
          <BrandMark className="mx-auto" />
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 text-amber-900 text-[13px] font-semibold px-3 py-1">
            <Gift size={14} aria-hidden />
            מתנה לזוג
          </div>
          <h1 className="font-display text-3xl font-bold text-ink">{meta.event_name}</h1>
          {meta.event_date ? (
            <p className="text-ink-mute text-sm">{formatDate(meta.event_date)}</p>
          ) : null}
          <p className="text-ink-mute text-[14px] leading-relaxed max-w-sm mx-auto">
            בחרו סכום חופשי, ואז תעברו לתשלום בביט ישירות לזוג.
          </p>
        </header>

        <form onSubmit={(e) => void submit(e)} className="surface p-5 space-y-4">
          <div>
            <label className="block text-[13px] font-semibold text-ink mb-1.5" htmlFor="gift-amount">
              סכום המתנה (₪)
            </label>
            <input
              id="gift-amount"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
              placeholder="הקלידו כל סכום"
              className="field w-full text-center text-3xl font-bold tracking-wide py-4"
              autoFocus
            />
            <div className="mt-2.5 flex flex-wrap gap-2 justify-center">
              {QUICK.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => setAmount(String(q))}
                  className={`rounded-full px-3 py-1.5 text-[13px] font-semibold border transition ${
                    amountNum === q
                      ? 'bg-sage-600 text-white border-sage-600'
                      : 'bg-white text-ink border-line hover:border-sage-400'
                  }`}
                >
                  ₪{q.toLocaleString('he-IL')}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[13px] font-semibold text-ink mb-1.5" htmlFor="gift-name">
              השם שלכם
            </label>
            <input
              id="gift-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="שם מלא"
              className="field w-full"
              autoComplete="name"
            />
          </div>

          <div>
            <label className="block text-[13px] font-semibold text-ink mb-1.5" htmlFor="gift-phone">
              טלפון <span className="font-normal text-ink-faint">(אופציונלי)</span>
            </label>
            <input
              id="gift-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="050…"
              className="field w-full"
              inputMode="tel"
              autoComplete="tel"
              dir="ltr"
            />
          </div>

          <div>
            <label className="block text-[13px] font-semibold text-ink mb-1.5" htmlFor="gift-blessing">
              ברכה <span className="font-normal text-ink-faint">(אופציונלי)</span>
            </label>
            <textarea
              id="gift-blessing"
              value={blessing}
              onChange={(e) => setBlessing(e.target.value)}
              placeholder="מזל טוב…"
              className="field w-full min-h-[88px] resize-y"
              rows={3}
            />
          </div>

          {error ? (
            <div className="rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2">
              {error}
            </div>
          ) : null}

          {!meta.bit_ready ? (
            <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm px-3 py-2">
              הזוג עדיין לא הגדיר ביט — אפשר לרשום את המתנה, והם יראו אותה באפליקציה.
            </div>
          ) : null}

          <button
            type="submit"
            disabled={submitting}
            className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {submitting ? 'שומר…' : meta.bit_ready ? 'המשך לתשלום בביט' : 'רישום מתנה'}
            {!submitting && meta.bit_ready ? <ExternalLink size={16} aria-hidden /> : null}
          </button>

          <p className="text-[12px] text-ink-faint text-center leading-relaxed">
            הסכום חופשי לבחירתכם. התשלום עובר ישירות לזוג בביט — לא דרך DoWe.
          </p>
        </form>
      </div>
    </div>
  );
}
